import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Image as ImageIcon,
  Loader2,
  MessageCircle,
  Send,
  Trash2,
} from 'lucide-react';
import { WRITING_TYPE_LABELS, type AssessmentGrade } from '@/lib/assessment';
import { formatDisplayName } from '@/lib/pupils';
import { FLUENCY_METRICS, type FluencyScore } from '@/lib/fluency';
import { ExtractedTextToggle, GradeWithJustification } from '@/components/assessment/ResultParts';

// ---- Shared types (exported for Assessments.tsx) --------------------------

// FluencyScore and FLUENCY_METRICS live in lib/fluency; re-exported here for existing importers.
export { FLUENCY_METRICS, type FluencyScore } from '@/lib/fluency';

export interface StructuredAssessment {
  id: string;
  ai_grade: AssessmentGrade | null;
  final_grade: AssessmentGrade | null;
  fluency_score: FluencyScore | null;
  justification: string | null;
  extracted_text: string | null;
  status: 'pending' | 'accepted' | 'modified';
  created_at: string;
  possible_duplicate: boolean | null;
  writing_samples: {
    title: string;
    writing_type: string;
    image_url: string | null;
    image_urls: string[] | null;
    pupils: {
      first_name: string;
      last_name: string;
      anonymisation: 'full_name' | 'first_initial' | 'initials_only';
      classes?: {
        name: string;
        year_groups?: {
          name: string;
          key_stages?: { name: string } | null;
        } | null;
      } | null;
    } | null;
  } | null;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface RowState {
  status: 'pending' | 'accepted' | 'modified';
  final_grade: AssessmentGrade | null;
  ai_grade: AssessmentGrade | null;
  gradeAction: 'none' | 'accepting' | 'accepted' | 'overriding' | 'overridden';
  overrideGrade: AssessmentGrade | null;
  isActioning: boolean;
  chatOpen: boolean;
  chatMessages: ChatMessage[];
  chatInput: string;
  chatLoading: boolean;
  imageOpen: boolean;
  fluencyOpen: boolean;
  extractedTextOpen: boolean;
  deleting: boolean;
}

// ---- Props ----------------------------------------------------------------

interface AssessmentCardProps {
  assessment: StructuredAssessment;
  rowState: RowState;
  canManage: boolean;
  onAccept: () => void;
  onStartOverride: () => void;
  onConfirmOverride: () => void;
  onCancelOverride: () => void;
  onOverrideGradeChange: (grade: AssessmentGrade) => void;
  onToggleChat: () => void;
  onSendChat: () => void;
  onChatInputChange: (value: string) => void;
  onToggleImage: () => void;
  onFluencyOpenChange: (open: boolean) => void;
  onToggleExtractedText: () => void;
  onDelete: () => void;
  chatBottomRef: (el: HTMLDivElement | null) => void;
}

// ---- Component ------------------------------------------------------------

export default function AssessmentCard({
  assessment: a,
  rowState: rs,
  canManage,
  onAccept,
  onStartOverride,
  onConfirmOverride,
  onCancelOverride,
  onOverrideGradeChange,
  onToggleChat,
  onSendChat,
  onChatInputChange,
  onToggleImage,
  onFluencyOpenChange,
  onToggleExtractedText,
  onDelete,
  chatBottomRef,
}: AssessmentCardProps) {
  const [copied, setCopied] = useState(false);
  const [signedImageUrls, setSignedImageUrls] = useState<string[]>([]);

  useEffect(() => {
    const ws = a.writing_samples;
    if (!ws?.image_url) return;
    const paths: string[] =
      Array.isArray(ws.image_urls) && ws.image_urls.length > 0
        ? ws.image_urls
        : ws.image_url
          ? [ws.image_url]
          : [];
    if (paths.length === 0) return;

    Promise.all(
      paths.map((p) => supabase.storage.from('writing-samples').createSignedUrl(p, 3600))
    ).then((results) => {
      setSignedImageUrls(results.map((r) => r.data?.signedUrl ?? '').filter(Boolean));
    });
  }, [a.writing_samples]);

  function copyAssessment() {
    if (!a.justification) return;
    navigator.clipboard.writeText(a.justification).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const pupil = a.writing_samples?.pupils;
  const displayName = pupil
    ? formatDisplayName(pupil.first_name, pupil.last_name, pupil.anonymisation)
    : '—';
  const yearGroup = a.writing_samples?.title?.split(' – ')[1] ?? '—';
  const writingType = a.writing_samples?.writing_type
    ? WRITING_TYPE_LABELS[a.writing_samples.writing_type as keyof typeof WRITING_TYPE_LABELS] ??
      a.writing_samples.writing_type
    : '—';
  const displayGrade = rs.final_grade ?? rs.ai_grade;

  // ---- Status badge -------------------------------------------------------

  const statusBadge = (() => {
    if (rs.status === 'pending') {
      return <Badge variant="secondary">Pending</Badge>;
    }

    if (rs.status === 'accepted' && rs.gradeAction !== 'overriding' && canManage) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs gap-1 bg-success/10 border-success text-success hover:bg-success/20"
            >
              Accepted <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onStartOverride}>
              Override Grade
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // 'modified' or post-override
    return (
      <Badge variant="outline" className="text-warning border-warning">
        Manual Override
      </Badge>
    );
  })();

  // ---- Override form (shared by pending + accepted paths) ------------------

  const overrideForm = (
    <div className="space-y-2">
      <Select
        value={rs.overrideGrade ?? ''}
        onValueChange={(v) => onOverrideGradeChange(v as AssessmentGrade)}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select grade..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="working_towards">Working Towards (WTS)</SelectItem>
          <SelectItem value="expected">Expected (EXS)</SelectItem>
          <SelectItem value="greater_depth">Greater Depth (GDS)</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="destructive"
          className="flex-1"
          disabled={rs.isActioning || !rs.overrideGrade}
          onClick={onConfirmOverride}
        >
          {rs.isActioning ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Confirming...</>
          ) : (
            'Confirm Override'
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="flex-1"
          disabled={rs.isActioning}
          onClick={onCancelOverride}
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <Card className={rs.deleting ? 'opacity-0 scale-95 transition-all duration-200 pointer-events-none' : 'transition-all duration-200'}>
      {/* Header */}
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-start justify-between gap-2">
          <span className="leading-snug">{displayName}</span>
          <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
            {/* Fluency popover */}
            {a.fluency_score && (() => {
              const avg = (
                (a.fluency_score.syntactic_maturity +
                  a.fluency_score.textual_cohesion +
                  a.fluency_score.lexical_precision) / 3
              ).toFixed(1);
              return (
                <Popover open={rs.fluencyOpen} onOpenChange={onFluencyOpenChange}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="text-xs h-6 px-2">
                      Fluency {avg}/10
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 space-y-3 text-sm">
                    <p className="font-medium">Fluency Breakdown</p>
                    <div className="grid grid-cols-3 gap-3">
                      {FLUENCY_METRICS.map(({ key, label, desc }) => (
                        <div key={key} className="space-y-1.5">
                          <div className="space-y-0.5">
                            <p className="text-xs font-medium leading-tight">{label}</p>
                            <p className="text-xs text-muted-foreground">{a.fluency_score![key]}/10</p>
                          </div>
                          <Progress value={a.fluency_score![key] * 10} className="h-1.5" />
                          <p className="text-xs text-muted-foreground leading-snug">{desc}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed w-full border-t pt-2 bg-muted/30 rounded px-2 py-1.5">
                      {a.fluency_score.summary}
                    </p>
                  </PopoverContent>
                </Popover>
              );
            })()}

            {displayGrade && (
              <GradeWithJustification grade={displayGrade} justification={a.justification} size="sm" />
            )}

            {a.possible_duplicate === true && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge variant="outline" className="text-warning border-warning">
                      Possible Duplicate
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="z-[100] max-w-xs">
                  This looks like a piece already assessed for this pupil in the last 30 days — check before relying on the grade.
                </TooltipContent>
              </Tooltip>
            )}

            {statusBadge}

            {/* Copy */}
            {a.justification && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                onClick={copyAssessment}
                aria-label="Copy assessment text"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            )}

            {canManage && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                onClick={onDelete}
                aria-label="Delete assessment"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </CardTitle>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{yearGroup} · {writingType}</span>
          <span>{new Date(a.created_at).toLocaleDateString('en-GB')}</span>
        </div>
      </CardHeader>

      {/* Pending: Accept / Override */}
      {canManage && rs.status === 'pending' && (
        <CardContent className="pt-0 space-y-2">
          {rs.gradeAction === 'none' && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="default"
                className="flex-1"
                disabled={rs.isActioning}
                onClick={onAccept}
              >
                Accept Grade
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-warning border-warning"
                disabled={rs.isActioning}
                onClick={onStartOverride}
              >
                Override Grade
              </Button>
            </div>
          )}
          {rs.gradeAction === 'accepting' && (
            <div className="flex gap-2">
              <Button size="sm" disabled variant="default" className="flex-1">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accepting...
              </Button>
              <Button size="sm" disabled variant="outline" className="flex-1 text-warning border-warning">
                Override Grade
              </Button>
            </div>
          )}
          {rs.gradeAction === 'overriding' && (
            <CardContent className="pt-0">{overrideForm}</CardContent>
          )}
        </CardContent>
      )}

      {/* Accepted but override initiated via badge dropdown */}
      {canManage && rs.status === 'accepted' && rs.gradeAction === 'overriding' && (
        <CardContent className="pt-0">{overrideForm}</CardContent>
      )}

      {/* Extracted Text */}
      {a.extracted_text && (
        <CardContent className="pt-0">
          <ExtractedTextToggle text={a.extracted_text} open={rs.extractedTextOpen} onToggle={onToggleExtractedText} />
        </CardContent>
      )}

      {/* View Writing Sample */}
      {a.writing_samples?.image_url && (() => {
        if (signedImageUrls.length === 0) return null;
        const isMulti = signedImageUrls.length > 1;

        return (
          <CardContent className="pt-0">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between text-sm text-muted-foreground"
              onClick={onToggleImage}
            >
              <span className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                View Writing Sample{isMulti ? ` (${signedImageUrls.length} pages)` : ''}
              </span>
              {rs.imageOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            {rs.imageOpen && (
              <div className="mt-2 overflow-x-auto">
                <div className={isMulti ? 'flex gap-3 pb-2' : undefined}>
                  {signedImageUrls.map((url, i) => (
                    <div key={i} className={isMulti ? 'relative flex-shrink-0' : undefined}>
                      {isMulti && (
                        <span className="absolute top-1 left-1 bg-black/50 text-white text-[10px] leading-none rounded px-1 py-0.5 z-10">
                          {i + 1}/{signedImageUrls.length}
                        </span>
                      )}
                      <img
                        src={url}
                        alt={isMulti ? `Writing sample page ${i + 1}` : 'Writing sample'}
                        className={
                          isMulti
                            ? 'h-[400px] w-auto rounded-md border object-contain'
                            : 'w-full rounded-md border object-contain max-h-[500px]'
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        );
      })()}

      {/* Discuss with AI — accepted / modified only */}
      {rs.status !== 'pending' && (
        <CardContent className="pt-0">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between text-sm text-muted-foreground"
            onClick={onToggleChat}
          >
            <span className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Discuss with AI
            </span>
            {rs.chatOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          {rs.chatOpen && (
            <div className="mt-2 space-y-2">
              <ScrollArea className="h-48 rounded-md border p-3">
                {rs.chatMessages.length === 0 && !rs.chatLoading ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    Ask a question about this assessment.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {rs.chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`rounded-lg px-3 py-2 max-w-[80%] text-sm ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {rs.chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-lg px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" /> Thinking...
                        </div>
                      </div>
                    )}
                    <div ref={chatBottomRef} />
                  </div>
                )}
              </ScrollArea>
              <div className="flex gap-2">
                <Textarea
                  placeholder="Ask a question about this assessment..."
                  className="resize-none text-sm min-h-[60px]"
                  value={rs.chatInput}
                  disabled={rs.chatLoading}
                  onChange={(e) => onChatInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSendChat();
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="self-end"
                  disabled={rs.chatLoading || !rs.chatInput.trim()}
                  onClick={onSendChat}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
